import React, { useCallback, useMemo, useState } from "react";
import BadgeMultiSelect from "../../common/BadgeMultiSelect/BadgeMultiSelect";
import DateRangeFilter from "../../common/DateRangeFilter";
import { getAvatarColor, getInitials } from "../../../utils/avatarUtils";
import { resolveScheduleAuditDisplay } from "./scheduleAuditLabels";
import ScheduleActivityAiSummaryButton from "./ScheduleActivityAiSummaryButton";
import ScheduleEcommAuditViewLink from "./ScheduleEcommAuditViewLink";
import {
  SCHEDULE_ACTIVITY_DATE_PRESETS,
  SCHEDULE_ACTIVITY_EMPTY_DATE_RANGE,
  SCHEDULE_ACTIVITY_EMPTY_FILTERS,
  exportScheduleAuditLogToExcel,
  filterScheduleAuditLog,
  formatScheduleAuditWhen,
  getScheduleAuditFieldOptions,
  getScheduleAuditUserOptions,
  hasActiveScheduleAuditFilters,
} from "./scheduleActivityLogUtils";

export default function ScheduleActivityLogPanel({
  auditLog = [],
  loading = false,
  onRefresh,
  variant = "sidebar",
  studyLabel = "schedule",
  wizard,
  cohort,
  scheduleCreated = false,
  visitBuilderMode,
  visitCohorts,
  studyScheduleId,
  organizationId,
  createdBy,
  isScheduleLive = false,
}) {
  const isEmbedded = variant === "embedded";
  const [filters, setFilters] = useState(SCHEDULE_ACTIVITY_EMPTY_FILTERS);
  const [exporting, setExporting] = useState(false);

  const userOptions = useMemo(() => getScheduleAuditUserOptions(auditLog), [auditLog]);
  const fieldOptions = useMemo(() => getScheduleAuditFieldOptions(auditLog), [auditLog]);
  const filteredLog = useMemo(
    () => filterScheduleAuditLog(auditLog, filters),
    [auditLog, filters],
  );
  const activeFilters = hasActiveScheduleAuditFilters(filters);

  const updateFilters = (patch) => {
    setFilters((current) => ({ ...current, ...patch }));
  };

  const handleClearFilters = () => {
    setFilters({
      ...SCHEDULE_ACTIVITY_EMPTY_FILTERS,
      dateRange: { ...SCHEDULE_ACTIVITY_EMPTY_DATE_RANGE },
    });
  };

  const handleExport = useCallback(async () => {
    if (!filteredLog.length || exporting) return;
    setExporting(true);
    try {
      await exportScheduleAuditLogToExcel(filteredLog, { studyLabel });
    } finally {
      setExporting(false);
    }
  }, [exporting, filteredLog, studyLabel]);

  return (
    <aside
      className={`schedule-activity-log${
        isEmbedded ? " schedule-activity-log--embedded" : ""
      }`}
      aria-label="Schedule activity log"
    >
      <div className="schedule-activity-log__head">
        <span>Activity log</span>
        <div className="schedule-activity-log__head-actions">
          <ScheduleActivityAiSummaryButton
            auditLog={auditLog}
            wizard={wizard}
            cohort={cohort}
            scheduleCreated={scheduleCreated}
            visitBuilderMode={visitBuilderMode}
            visitCohorts={visitCohorts}
            studyScheduleId={studyScheduleId}
            organizationId={organizationId}
            createdBy={createdBy}
            isScheduleLive={isScheduleLive}
          />
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm schedule-activity-log__export"
            onClick={handleExport}
            disabled={loading || exporting || filteredLog.length === 0}
            title="Export filtered activity to Excel"
          >
            <i className="fas fa-file-excel" aria-hidden />
            <span>{exporting ? "Exporting…" : "Export Excel"}</span>
          </button>
          {onRefresh ? (
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm schedule-activity-log__refresh-btn"
              onClick={onRefresh}
              disabled={loading}
              title="Refresh activity log"
            >
              <i className={`fas fa-sync-alt${loading ? " fa-spin" : ""}`} aria-hidden />
              <span>Refresh</span>
            </button>
          ) : null}
        </div>
      </div>

      <div className="schedule-activity-log__layout">
        <div className="schedule-activity-log__main">
          <div className="schedule-activity-log__body">
            {loading && auditLog.length === 0 ? (
              <div className="schedule-activity-log__empty">Loading activity…</div>
            ) : auditLog.length === 0 ? (
              <div className="schedule-activity-log__empty">No activity recorded yet.</div>
            ) : filteredLog.length === 0 ? (
              <div className="schedule-activity-log__empty">
                No activity matches the current filters.
              </div>
            ) : (
              <ul className="schedule-activity-log__list">
                {filteredLog.map((row, index) => {
                  const {
                    actionDisplay,
                    fieldDisplay,
                    detailDisplay,
                    reasonDisplay,
                    showValueDiff,
                    showEcommView,
                    ecommDiff,
                    valueDiffRows,
                    oldDisplay,
                    newDisplay,
                    actorLabel,
                  } = resolveScheduleAuditDisplay(row);
                  const key = row.Id || `${row.At}-${index}`;

                  return (
                    <li key={key} className="schedule-activity-log__item">
                      <div className="schedule-activity-log__action-line">
                        {actionDisplay}
                      </div>
                      <div className="schedule-activity-log__field-line">
                        <span className="schedule-activity-log__field-caption">Field:</span>
                        <span
                          className="schedule-activity-log__field-name"
                          title={fieldDisplay}
                        >
                          {fieldDisplay}
                        </span>
                      </div>
                      {reasonDisplay ? (
                        <div className="schedule-activity-log__reason">
                          <span className="schedule-activity-log__reason-label">
                            Reason
                          </span>
                          <span className="schedule-activity-log__reason-text">
                            {reasonDisplay}
                          </span>
                        </div>
                      ) : null}
                      {detailDisplay || showEcommView ? (
                        <div className="schedule-activity-log__detail-row">
                          {detailDisplay ? (
                            <p className="schedule-activity-log__detail-line">
                              {detailDisplay}
                            </p>
                          ) : null}
                          {showEcommView ? (
                            <ScheduleEcommAuditViewLink
                              diff={ecommDiff}
                              variant={variant}
                            />
                          ) : null}
                        </div>
                      ) : null}
                      {showValueDiff && Array.isArray(valueDiffRows) && valueDiffRows.length > 0 ? (
                        <div className="schedule-activity-log__diff-list">
                          {valueDiffRows.map((diffRow) => (
                            <div
                              key={diffRow.label}
                              className="schedule-activity-log__diff-row"
                            >
                              <span
                                className="schedule-activity-log__diff-label"
                                title={diffRow.label}
                              >
                                {diffRow.label}
                              </span>
                              <div className="schedule-activity-log__diff-values">
                                {diffRow.oldValue ? (
                                  <span
                                    className="schedule-activity-log__old"
                                    title={diffRow.oldValue}
                                  >
                                    {diffRow.oldValue}
                                  </span>
                                ) : (
                                  <span className="schedule-activity-log__empty-value">—</span>
                                )}
                                <i
                                  className="fas fa-arrow-right schedule-activity-log__arrow"
                                  aria-hidden
                                />
                                {diffRow.newValue ? (
                                  <span
                                    className="schedule-activity-log__new"
                                    title={diffRow.newValue}
                                  >
                                    {diffRow.newValue}
                                  </span>
                                ) : (
                                  <span className="schedule-activity-log__empty-value">—</span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : showValueDiff ? (
                        <div className="schedule-activity-log__diff">
                          {oldDisplay ? (
                            <span
                              className="schedule-activity-log__old"
                              title={oldDisplay}
                            >
                              {oldDisplay}
                            </span>
                          ) : null}
                          {oldDisplay && newDisplay ? (
                            <i
                              className="fas fa-arrow-right schedule-activity-log__arrow"
                              aria-hidden
                            />
                          ) : null}
                          {newDisplay ? (
                            <span
                              className="schedule-activity-log__new"
                              title={newDisplay}
                            >
                              {newDisplay}
                            </span>
                          ) : null}
                        </div>
                      ) : null}
                      <div className="schedule-activity-log__footer">
                        <span
                          className="schedule-activity-log__avatar"
                          style={{ backgroundColor: getAvatarColor(actorLabel) }}
                          title={actorLabel}
                        >
                          {getInitials(actorLabel)}
                        </span>
                        <span className="schedule-activity-log__footer-meta">
                          {actorLabel}
                          {" · "}
                          {formatScheduleAuditWhen(row.At)}
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        <div className="schedule-activity-log__filters-panel">
          <div className="schedule-activity-log__filters-title">Filters</div>

          <label className="schedule-activity-log__filter-label">Search</label>
          <input
            type="search"
            className="form-control form-control-sm schedule-activity-log__filter-search"
            placeholder="Search anything…"
            value={filters.query}
            onChange={(event) => updateFilters({ query: event.target.value })}
            aria-label="Search activity log"
          />

          <label className="schedule-activity-log__filter-label">Date range</label>
          <DateRangeFilter
            value={filters.dateRange}
            onChange={(dateRange) => updateFilters({ dateRange })}
            placeholder="Activity date range"
            presetOptions={SCHEDULE_ACTIVITY_DATE_PRESETS}
            compact
          />

          <label className="schedule-activity-log__filter-label">Users</label>
          <BadgeMultiSelect
            options={userOptions}
            value={filters.users}
            onChange={(users) => updateFilters({ users })}
            emptyLabel="All users"
            addLabel="+ Add user"
            popoverTitle="Filter by user"
            popoverSubtitle="Select one or more users"
            showSearch
            compactSummary
            inlinePopover
          />

          <label className="schedule-activity-log__filter-label">Fields</label>
          <BadgeMultiSelect
            options={fieldOptions}
            value={filters.fields}
            onChange={(fields) => updateFilters({ fields })}
            emptyLabel="All fields"
            addLabel="+ Add field"
            popoverTitle="Filter by field"
            popoverSubtitle="Select one or more fields"
            showSearch
            compactSummary
            inlinePopover
          />

          {activeFilters ? (
            <button
              type="button"
              className="btn btn-link btn-sm schedule-activity-log__clear-filters"
              onClick={handleClearFilters}
            >
              Clear filters
            </button>
          ) : null}

          <div className="schedule-activity-log__filter-meta">
            Showing {filteredLog.length} of {auditLog.length}
          </div>
        </div>
      </div>
    </aside>
  );
}
